import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { CheckEventUrl/* , PublishEvent as PublishEventApi */ } from '../../../connection/create-event';
import { EventsState } from '../../../store/types';
import { showAlert } from '../../general-ui/alert/alert-service';
import TextInput, { Validation } from '../../general-ui/text-input/text';
import ModalComponent from '../../general-ui/modal/modal';
import WaitingIndicator from '../../general-ui/waiting-indicator/waiting-indicator';
import { publishEvent, updatePublishedUrl } from '../../../store/actions/admin/create-event';
import usePrevious from '../../../utils/use-previous';
import { addChecklistItem } from '../../../store/actions/authentication';
import { ChecklistTitles, EAssistantPhase, ProgressHeaders, skipAssistantWarning } from '../../../store/reducers/admin/assistant';
import { toggleAssistant, updatePhase } from '../../../store/actions/admin/assistant';
import Checkbox from '../../general-ui/checkbox/checkbox';
import { COLORS } from '../../general-ui/icon';
import { getPrefixUrl } from '../../../utils/utils';
import { updateAdminEvents } from '../../../store/actions/admin/events';
import { Status } from '../../../types/event';

interface Props {
	open: boolean;
	close: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
enum ValidStates {
	unknown,
	valid,
	invalid
}

export default function PublishEvent({
	open,
	close
}: Props): JSX.Element {
	const {
		AuthReducer: {
			token,
			channels,
			user
		},
		CreateEventReducer: {
			workingEvent,
			publishingEvent,
			publishedUrl
		},
		AssistantReducer: {
			checklistOptions
		}
	} = useSelector((state: EventsState) => state);

	const dispatch = useDispatch();

	const wasPublishing = usePrevious(publishingEvent);
	/*

	Custom domains set aside until the future. 

	*/
	// const [custom, setCustom] = useState(false);
	const [valid, setValid] = useState(Validation.normal);
	const [errorMsg, setErrorMsg] = useState("");
	const [publishing, setPublishing] = useState(false);
	// const history = useHistory();
	const [checklistWarning, setChecklistWarning] = useState(false);
	const [skipWarningChecked, setSkipWarningChecked] = useState(false);
	const [filteredItems, setFilteredItems] = useState<string[]>([]);
	const abortUrlCheck = useRef<AbortController | null>(null);

	const channel = useMemo(() => {
		return channels.find((channel) => channel.channel === user!.active_channel);
	}, [channels, user]);

	const [url, setUrl] = useState(workingEvent?.name.toLowerCase().replace(/[^0-9a-z]/g, '-') ?? '');

	useEffect(() => {
		if (publishedUrl) setUrl(publishedUrl);
	}, [publishedUrl, token, workingEvent?.uuid]);

	function handleText({ target: { value } }: React.ChangeEvent<HTMLInputElement>) {
		setUrl(value);
	}

	function handleKeyUp(e: React.KeyboardEvent<HTMLInputElement>) {
		checkUrl((e.target as HTMLInputElement).value);
	}

	const checkUrl = useCallback(async (url: string) => {
		try {
			//use an abort controller here if someone types super fast, we don't want the 
			//fetch requests to arrive out of order and mess things up. 
			//so if the previous request is still in flight, abort the request and send a new one.
			if (abortUrlCheck.current) abortUrlCheck.current.abort();

			if (!url) {
				setValid(Validation.error);
				setErrorMsg("Please enter a URL.");
				return;
			}

			const [controller, checkUrlPromise] = CheckEventUrl(token!, channel!.url, url, workingEvent!.event!, workingEvent!.channel);
			abortUrlCheck.current = controller;

			checkUrlPromise.then(({ exists }) => {
				setValid(exists ? Validation.error : Validation.ok);
				setErrorMsg("This URL already exists. Please enter a unique URL.");
			}).catch(e => {
				console.error(e);
			});
		} catch (e) {
			console.error(e);
			showAlert({
				message: "Error Checking URL",
				description: "We were unable to check that event url. Please try again",
				type: "error"
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [token, workingEvent]);

	async function publish() {

		setChecklistWarning(false);
		setPublishing(true);

		const _workingEvent = { ...workingEvent! };
		_workingEvent.status = Status.Published;

		batch(() => {
			dispatch(publishEvent(token!, workingEvent!.uuid, url));
			dispatch(updateAdminEvents(_workingEvent));
			dispatch(updatePublishedUrl(url));
		});

		// ASSISTANT CHECKLIST ITEM: complete users checklist item for publishing an event
		if (user && !user.onboarded.includes(ChecklistTitles.publishEvent))
			dispatch(addChecklistItem(token!, user.id, user.onboarded, ChecklistTitles.publishEvent));

		// if skip warning checked, updated user profile
		if (user && skipWarningChecked && !user.onboarded.includes(skipAssistantWarning))
			dispatch(addChecklistItem(token!, user.id, user.onboarded, skipAssistantWarning));
	}

	useEffect(() => {
		if (workingEvent && !url) {
			setUrl(workingEvent.name.toLowerCase().replace(/[^0-9a-z]/g, '-') ?? '');
			checkUrl(workingEvent.name.toLowerCase().replace(/[^0-9a-z]/g, '-') ?? '');
		}
	}, [workingEvent, url, checkUrl]);

	useEffect(() => {
		if (wasPublishing && !publishingEvent) {
			setPublishing(false);
			close();
		}
	}, [publishingEvent, wasPublishing, close]);

	useEffect(() => {
		if (open) {
			checkUrl(url);
		}

	}, [open, checkUrl, url]);

	const closeWarning = useCallback(() => {
		setChecklistWarning(false);
		batch(() => {
			dispatch(updatePhase(EAssistantPhase.PROGRESS));
			dispatch(toggleAssistant(true));
		});

		// if skip warning checked, updated user profile so warning won't appear again
		if (user && skipWarningChecked && !user.onboarded.includes(skipAssistantWarning))
			dispatch(addChecklistItem(token!, user.id, user.onboarded, skipAssistantWarning));

		close();
	}, [close, dispatch, skipWarningChecked, token, user]);

	// filter all checklist items for ones that match with current working event settings
	useEffect(() => {
		const items = [];
		for (let i = 0; i < checklistOptions.length; i++) {
			// if registration turned off, remove registration items
			if (!workingEvent?.registration_on && checklistOptions[i].type === ProgressHeaders.registration)
				continue;
			// if no homepage, remove landing and homepage checklist items
			if (!workingEvent?.homepage && ((checklistOptions[i].type === ProgressHeaders.eventHomepage) || (checklistOptions[i].type === ProgressHeaders.landingPage)))
				continue;
			// remove final publish item for completion requirement
			if (checklistOptions[i].title === ChecklistTitles.publishEvent)
				continue;
			items.push(checklistOptions[i].title);
		}
		if (items.length)
			setFilteredItems(items);
	}, [workingEvent, checklistOptions]);

	// iterate over the current filtered checklist items, if all are complete return true
	const checkIsComplete = () => {
		if (user?.onboarded.length && filteredItems.length) {
			let complete = true;
			for (let i = 0; i < filteredItems.length; i++) {
				if (!user.onboarded.includes(filteredItems[i])) {
					complete = false;
					break;
				}
			}
			return complete;
		}
		return false;
	};

	// checks to see if assistant checklist has been completed, if
	const checkAssistant = () => {
		if (
			user &&
			!user.onboarded.includes(skipAssistantWarning) &&
			!checkIsComplete()
		)
			setChecklistWarning(true);
		else {
			publish();
		}
	};

	return (
		<>
			<ModalComponent
				cancellable={false}
				closeable={false}
				open={open}
				onRequestClose={close}
				title={"Publish Event"}
				subtitle={"Please enter a URL for your event."}
				footer={(
					<>
						<button onClick={close} style={{ marginRight: 16 }}>Cancel</button>
						<button disabled={valid !== Validation.ok || publishing} onClick={checkAssistant} className="primary">{publishing ? <WaitingIndicator /> : "Publish"}</button>
					</>
				)}
			>
				{open ? (
					<div className="publish-event">

						<TextInput
							onChange={handleText}
							label="Event URL"
							placeholder={"Enter event url"}
							defaultValue={url}
							prefix={`${getPrefixUrl(channel!.url)}/`}
							valid={valid}
							onKeyUp={handleKeyUp}
						/>

						{valid === Validation.error && (
							<p style={{
								fontSize: 12,
								fontWeight: 'bold',
								color: '#FF375F',
								margin: 0,
								padding: 0,
								position: 'absolute',
								marginTop: -24,
								marginLeft: 18,
								animation: '150ms fadeIn'
							}}>{errorMsg}</p>
						)}
					</div>
				) : (<div />)}

			</ModalComponent>
			<ModalComponent
				cancellable={false}
				closeable={false}
				onRequestClose={closeWarning}
				open={checklistWarning}
				title={"Assistant checklist not completed"}
				footer={(
					<>
						<Checkbox
							checked={skipWarningChecked}
							value={"skipWarningChecked"}
							onChange={() => setSkipWarningChecked(!skipWarningChecked)}
							greyCheckbox={true}
							activeColor={COLORS.CYAN}
						/>
						<label style={{ marginLeft: 8 }}> Don't show this message again</label>
						<button style={{ marginLeft: 'auto' }} onClick={closeWarning}>Continue Editing</button>
						<button className="primary" onClick={publish}>Publish</button>
					</>
				)}
			>
				<p style={{ marginTop: 16 }}>You have not completed your Event checklist. Would you like to continue publishing?</p>
			</ModalComponent>
		</>
	);
}